import React from 'react';
import { DollarSign, CalendarDays, Server, Users, TrendingUp, Database, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости проекта Big Data
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Вспомогательная функция для форматирования валюты
  const formatCurrencyWithSymbol = (value) => {
    const currencySymbol = getCurrencySymbol(formData.currency);
    const formattedValue = formatNumber(value, 2);
    return `${formattedValue} ${currencySymbol}`;
  };

  // Получение символа валюты
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode.toLowerCase()) {
      case 'usd': return '$';
      case 'eur': return '€';
      case 'rub': return '₽';
      case 'gbp': return '£';
      default: return currencyCode.toUpperCase();
    }
  };

  return (
    <ToolResult title="Результаты расчета стоимости проекта">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость проекта */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость проекта
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.totalCost)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            За весь период проекта ({results.projectDuration} {pluralizeMonths(results.projectDuration)})
          </p>
        </div>

        {/* Ежемесячная стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Ежемесячная стоимость
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrencyWithSymbol(results.monthlyCost)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              в месяц
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Усредненные ежемесячные затраты
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Инфраструктура" 
          value={`${formatCurrencyWithSymbol(results.infrastructureCost)} (${formatNumber(results.percentageBreakdown.infrastructure, 1)}%)`} 
          color="blue" 
          icon={<Server className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Персонал" 
          value={`${formatCurrencyWithSymbol(results.personnelCost)} (${formatNumber(results.percentageBreakdown.personnel, 1)}%)`} 
          color="green" 
          icon={<Users className="w-4 h-4"/>}
        />
        
        {results.additionalCosts > 0 && (
          <ResultItem 
            label="Дополнительные расходы" 
            value={`${formatCurrencyWithSymbol(results.additionalCosts)} (${formatNumber(results.percentageBreakdown.additional, 1)}%)`} 
            color="purple" 
            icon={<TrendingUp className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Непредвиденные расходы" 
          value={`${formatCurrencyWithSymbol(results.contingencyAmount)} (${formatNumber(results.percentageBreakdown.contingency, 1)}%)`} 
          color="orange" 
          icon={<AlertCircle className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Объем данных" 
          value={`${formatNumber(results.dataVolumeInTB, 2)} ТБ`} 
          icon={<Database className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип проекта" 
          value={getProjectTypeName(formData.projectType)} 
        />
        
        <ResultItem 
          label="Облачный провайдер" 
          value={getCloudProviderName(formData.cloudProvider)} 
        />

        {/* Рекомендации по оптимизации */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендации по оптимизации расходов</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Основываясь на вашем проекте, мы рекомендуем:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                {generateRecommendations(formData, results).map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "месяц"
const pluralizeMonths = (months) => {
  const lastDigit = months % 10;
  const lastTwoDigits = months % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'месяцев';
  if (lastDigit === 1) return 'месяц';
  if (lastDigit >= 2 && lastDigit <= 4) return 'месяца';
  return 'месяцев';
};

// Вспомогательная функция для получения названия типа проекта
const getProjectTypeName = (projectTypeId) => {
  const projectTypes = {
    'analytics': 'Аналитика данных',
    'ml': 'Машинное обучение',
    'realtime': 'Обработка в реальном времени',
    'datawarehouse': 'Хранилище данных',
    'dataquality': 'Управление качеством данных',
    'dataintegration': 'Интеграция данных'
  };
  
  return projectTypes[projectTypeId] || projectTypeId;
};

// Вспомогательная функция для получения названия облачного провайдера
const getCloudProviderName = (providerId) => {
  const providers = {
    'aws': 'Amazon Web Services (AWS)',
    'azure': 'Microsoft Azure',
    'gcp': 'Google Cloud Platform (GCP)',
    'onpremise': 'On-premise решение'
  };
  
  return providers[providerId] || providerId;
};

// Генерация рекомендаций на основе параметров проекта
const generateRecommendations = (formData, results) => {
  const recommendations = [];
  
  // Рекомендации по объему данных
  if (results.dataVolumeInTB > 100) {
    recommendations.push('Использовать систему хранения с автоматическим перемещением данных между горячими и холодными хранилищами для оптимизации затрат.');
  }
  
  // Рекомендации по типу проекта
  if (formData.projectType === 'ml') {
    recommendations.push('Рассмотреть использование предварительно обученных моделей для снижения затрат на вычисления при машинном обучении.');
  } else if (formData.projectType === 'realtime') {
    recommendations.push('Оптимизировать размер обрабатываемых пакетов данных для снижения затрат на обработку в реальном времени.');
  }
  
  // Рекомендации по облачному провайдеру
  if (formData.cloudProvider === 'aws' || formData.cloudProvider === 'azure' || formData.cloudProvider === 'gcp') {
    recommendations.push('Использовать зарезервированные инстансы для длительных проектов, что может снизить инфраструктурные затраты до 70%.');
  } else if (formData.cloudProvider === 'onpremise') {
    recommendations.push('Рассмотреть гибридное решение с использованием облачных ресурсов для пиковых нагрузок.');
  }
  
  // Общие рекомендации
  recommendations.push('Регулярно анализировать использование ресурсов и отключать неиспользуемые сервисы.');
  
  // Если список слишком короткий, добавляем общие рекомендации
  if (recommendations.length < 3) {
    recommendations.push('Внедрить автоматическое масштабирование для оптимизации затрат в периоды низкой нагрузки.');
  }
  
  return recommendations;
};

export default ResultsOverview;